import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * Bundle Add to Cart Component
 * Handles adding multiple line items to cart based on bundle configuration
 * TrueNeeds V1 Design
 */
class BundleAddToCartComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  init() {
    this.button = this.querySelector('[data-add-to-cart-button]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.errorContainer = this.querySelector('.tn-bundle-add-to-cart__error');
    this.errorText = this.querySelector('.tn-bundle-add-to-cart__error-text');
    this.successContainer = this.querySelector('.tn-bundle-add-to-cart__success');
    this.spinner = this.querySelector('.tn-bundle-add-to-cart__spinner');
    this.buttonText = this.querySelector('.tn-bundle-add-to-cart__button-text');

    // Add click event listener manually
    if (this.button) {
      this.button.addEventListener('click', () => this.handleAddToCart());
    }

    // Add quantity button listeners if they exist
    const incrementBtn = this.querySelector('[data-quantity-plus]');
    const decrementBtn = this.querySelector('[data-quantity-minus]');

    if (incrementBtn) {
      incrementBtn.addEventListener('click', () => this.incrementQuantity());
    }

    if (decrementBtn) {
      decrementBtn.addEventListener('click', () => this.decrementQuantity());
    }

    this.isLoading = false;
  }

  incrementQuantity() {
    if (!this.quantityInput) return;
    const currentValue = parseInt(this.quantityInput.value, 10) || 1;
    const max = parseInt(this.quantityInput.getAttribute('max'), 10) || 10;
    if (currentValue < max) {
      this.quantityInput.value = currentValue + 1;
    }
  }

  decrementQuantity() {
    if (!this.quantityInput) return;
    const currentValue = parseInt(this.quantityInput.value, 10) || 1;
    const min = parseInt(this.quantityInput.getAttribute('min'), 10) || 1;
    if (currentValue > min) {
      this.quantityInput.value = currentValue - 1;
    }
  }

  async handleAddToCart() {
    if (this.isLoading) return;

    // Hide previous messages
    this.hideMessages();

    // Get and validate bundle selections
    const validation = this.validateBundleSelections();

    if (!validation.valid) {
      this.showError(validation.errors[0]);
      return;
    }

    // Get variant IDs for each pack
    const variantIds = this.getVariantIds(validation.selections);

    if (variantIds.some((id) => !id)) {
      this.showError('Selected variant not available');
      return;
    }

    // Start loading state
    this.setLoadingState(true);

    try {
      // Add items to cart with retry logic for rate limits
      const maxRetries = 3;
      let response;
      let lastError;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        response = await this.addToCart(variantIds);

        if (response.status === 429) {
          if (attempt < maxRetries) {
            const waitTime = Math.pow(2, attempt + 1) * 500; // 1s, 2s, 4s
            console.warn(`Rate limited (429). Retrying in ${waitTime}ms... (attempt ${attempt + 1}/${maxRetries})`);
            await new Promise((resolve) => setTimeout(resolve, waitTime));
            continue;
          }
          lastError = 'Too many requests. Please wait a moment and try again.';
          break;
        }

        if (response.ok) {
          const data = await response.json();
          this.handleSuccess(data);
          return;
        } else {
          const errorData = await response.json();
          this.showError(errorData.description || 'Failed to add to cart');
          return;
        }
      }

      if (lastError) {
        this.showError(lastError);
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showError('An error occurred. Please try again.');
    } finally {
      this.setLoadingState(false);
    }
  }

  validateBundleSelections() {
    const configurator = document.querySelector('bundle-configurator-component');

    if (!configurator) {
      return {
        valid: false,
        errors: ['Bundle configurator not found'],
      };
    }

    return configurator.validateSelections();
  }

  getVariantIds(selections) {
    const configurator = document.querySelector('bundle-configurator-component');
    if (!configurator) return [];

    // Get product variants data
    const variantsScript = configurator.querySelector('[data-product-variants]');
    if (!variantsScript) return [];

    const variants = JSON.parse(variantsScript.textContent);

    // Find variant ID for each pack selection
    const variantIds = [];

    for (const [packIndex, selection] of selections.entries()) {
      const { color, size } = selection;

      // Find matching variant
      const variant = variants.find((v) => {
        // Check if variant options match the selection
        const colorMatch = v.option1 === color || v.option2 === color || v.option3 === color;
        const sizeMatch = v.option1 === size || v.option2 === size || v.option3 === size;

        return colorMatch && sizeMatch && v.available;
      });

      variantIds.push(variant ? variant.id : null);
    }

    return variantIds;
  }

  async addToCart(variantIds) {
    // Prepare items array - one item per variant ID
    const items = variantIds.map((variantId) => ({
      id: variantId,
      quantity: 1,
    }));

    // Get all cart-items-component section IDs for re-rendering
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = [];
    cartItemsComponents.forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionsToUpdate.push(component.dataset.sectionId);
      }
    });

    console.log('🛒 bundle-add-to-cart: Found cart-items-components:', {
      count: cartItemsComponents.length,
      sectionIds: sectionsToUpdate,
    });

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items,
        sections: sectionsToUpdate.join(','),
        sections_url: window.location.pathname,
      }),
    });
  }

  handleSuccess(data) {
    // Show success message
    this.showSuccess();

    console.log('🎉 bundle-add-to-cart: Cart add successful:', {
      itemCount: data.item_count,
      sectionsReturned: data.sections ? Object.keys(data.sections) : 'none',
    });

    // Dispatch cart add event with sections data for immediate DOM update
    document.dispatchEvent(
      new CartAddEvent(data, this.id, {
        source: 'bundle-add-to-cart',
        sections: data.sections,
      })
    );

    // Open cart drawer if setting is enabled
    const openDrawer = this.dataset.openCartDrawer !== 'false';
    if (openDrawer) {
      // Try to open the cart drawer component
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        // Small delay to allow section morphing to complete
        setTimeout(() => cartDrawer.open(), 100);
      }
    }

    // Hide success message after 3 seconds
    setTimeout(() => {
      this.hideMessages();
    }, 3000);
  }

  setLoadingState(loading) {
    this.isLoading = loading;

    if (loading) {
      this.button.disabled = true;
      this.button.classList.add('tn-bundle-add-to-cart__button--loading');
    } else {
      this.button.disabled = false;
      this.button.classList.remove('tn-bundle-add-to-cart__button--loading');
    }
  }

  showError(message) {
    if (this.errorContainer && this.errorText) {
      this.errorText.textContent = message;
      this.errorContainer.style.display = 'flex';
    }
  }

  showSuccess() {
    if (this.successContainer) {
      this.successContainer.style.display = 'flex';
    }
  }

  hideMessages() {
    if (this.errorContainer) {
      this.errorContainer.style.display = 'none';
    }
    if (this.successContainer) {
      this.successContainer.style.display = 'none';
    }
  }
}

// Register custom element
if (!customElements.get('bundle-add-to-cart-component')) {
  customElements.define('bundle-add-to-cart-component', BundleAddToCartComponent);
}

export { BundleAddToCartComponent };
